import React from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './stripplot.Renderer';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { TpRectangleSelection } from '../../zoompan-canvas/helpers';

export function Stripplot({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const {
    definition,
    areaPoints,
    dataTable,
    dataCategories,
    dataValues,
    hoverLabelColInfo,
    dataHoverLabel,
    dataItemId,
    nameX,
    nameY,
    strips,
    itemCount,
    getItemIndexById,
    itemHPosits,
    colInfoValues,
    colInfoCategories,
    title,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config, widgetGlobalConfig);
  const stripCount = strips.length;

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id != 'strips') return null;
    let minDist = mappingInfo.mapsize(10);
    let selItemId = null;
    for (const strip of strips) {
      for (let i = 0; i < strip.memberItemNumbers.length; i++) {
        const itemIdx = strip.memberItemNumbers[i];
        const scr_x = mappingInfo.mapX.map((strip.positionIndex + strip.memberItemHPosits[i]) / stripCount);
        const scr_y = mappingInfo.mapY.map(dataValues[itemIdx]);
        const dst = Math.abs(scr_x - renderPositX) + Math.abs(scr_y - renderPositY);
        if (dst <= minDist) {
          selItemId = dataItemId[itemIdx];
          minDist = dst;
        }
      }
    }
    if (selItemId == null) return null;
    return {
      dataTableId: dataBinding.dataTableId,
      itemId: selItemId,
    };
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId || hoverInfo.catColumnId) return null;
    const hoverItemNr = getItemIndexById(hoverInfo.itemId);
    const labels: string[] = [];
    if (context.isActiveChart) {
      if (dataHoverLabel) labels.push(convertColumnDataToDisplayString(hoverLabelColInfo, dataHoverLabel[hoverItemNr]));
      labels.push(`${nameX}=${convertColumnDataToDisplayString(colInfoCategories, dataCategories[hoverItemNr])}`);
      labels.push(`${nameY}=${convertColumnDataToDisplayString(colInfoValues, dataValues[hoverItemNr])}`);
    }
    return {
      area: areaPoints,
      logicPosX: itemHPosits[hoverItemNr],
      logicPosY: dataValues[hoverItemNr],
      labels,
    };
  }

  function onRectangleSelection(rectangleSelection: TpRectangleSelection) {
    widgetContext.handleUserSelectionAction({
      type: TpEnSelectionFilterTypes.NUMERICALRANGE,
      dataTableId: dataTable.id,
      colId: colInfoValues.id,
      rangeMin: rectangleSelection.logicYMin,
      rangeMax: rectangleSelection.logicYMax,
    });
  }

  function onItemClicked(itemId: string) {
    widgetContext.handleOpenItem(dataTable.canonicalTypeId, itemId);
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        onRectangleSelection={onRectangleSelection}
        onItemClicked={onItemClicked}
        widgetContext={widgetContext}
        maxZoomXAbsolute={stripCount * 40}
        maxZoomYRelative={30}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
    </div>
  );
}
